import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Users, Maximize2, Minimize2, Home, Info } from 'lucide-react';
import { CalendarSharedGroupPanel } from './CalendarSharedGroupPanel';
import { GroupMiniGantt } from './GroupMiniGantt';
import { groupSummary } from './groupGantt';
import { useStaffAvailabilityForShift } from './StaffAvailabilityHelpers';
import { accidentalConflicts } from './shiftSheetRules';
import { liveAssignments } from './sharedDeliveryRules';

/**
 * UC-B-2 (UI-14, UI-15, D3) — shared delivery group sheet: sibling of the shift sheet (same 640 px / expand layout, no
 * route). Reuses CalendarSharedGroupPanel for every action (S3 endpoints); services and billing are NOT editable here.
 */
export function CalendarGroupSheet({ shift, open, onClose, staff, onUpdate, onOpenShift }) {
  const [expanded, setExpanded] = useState(false);
  const [group, setGroup] = useState(null);
  const [error, setError] = useState('');
  const gid = shift?.shared_group_id;

  const load = useCallback(async () => {
    if (!gid) { setGroup(null); return null; }
    try { const r = await api.get(`/shared-delivery/groups/${gid}`); const g = r.data?.data || null; setGroup(g); setError(''); return g; }
    catch (e) { setGroup(null); setError(e?.response?.data?.detail?.message || 'Could not load the shared delivery group'); return null; }
  }, [gid]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const workers = useMemo(() => liveAssignments(group).filter((a) => a.worker_id).map((a) => ({ id: String(a.worker_id), name: a.worker_name })), [group]);
  const { statusMap } = useStaffAvailabilityForShift(workers, { date: group?.window?.date, startTime: group?.window?.start_time, endTime: group?.window?.end_time, excludeShiftId: shift?.id });
  const conflicts = useMemo(() => accidentalConflicts(statusMap, workers, group?.member_shift_ids || []), [statusMap, workers, group?.member_shift_ids]);

  if (!shift) return null;
  const sum = groupSummary(group);
  const member = (sid) => (group?.members || []).find((m) => String(m.id) === String(sid));
  const openMember = (sid) => { const m = member(sid); if (m && onOpenShift) onOpenShift({ ...m, shared_group_id: gid }); };
  const width = expanded ? 'w-full sm:max-w-none' : 'w-full sm:w-[640px] sm:max-w-[640px]';

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className={`${width} overflow-y-auto p-0 px-6 flex flex-col`} data-testid="group-sheet" data-expanded={expanded ? 'true' : 'false'}>
        <SheetTitle className="sr-only">Shared delivery group</SheetTitle>
        <SheetDescription className="sr-only">Members, coverage and ratio of this shared delivery group</SheetDescription>
        <div className="sticky top-0 z-10 bg-background border-b -mx-6 px-6 pt-4 pb-3 space-y-1.5" data-testid="group-sheet-header">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-base font-semibold flex items-center gap-2"><Users className="w-4 h-4" /> {sum.typeLabel} group <span className="font-mono text-xs text-muted-foreground">#{String(gid || '').slice(-6)}</span></div>
              <div className="text-xs text-muted-foreground tabular-nums flex items-center gap-1.5 flex-wrap">
                {group?.window?.date}{group?.window && <span className="font-medium text-foreground" data-testid="group-sheet-window">{sum.window}</span>}
                {(shift.house_name || group?.house_name) && <span className="inline-flex items-center gap-1"><Home className="w-3 h-3" />{shift.house_name || group?.house_name}</span>}
                <span data-testid="group-sheet-counts">{sum.participants} participant{sum.participants === 1 ? '' : 's'} · {sum.workers} worker{sum.workers === 1 ? '' : 's'}</span>
              </div>
            </div>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hidden sm:inline-flex" onClick={() => setExpanded((v) => !v)} title={expanded ? 'Dock to the side' : 'Expand to full page'} data-testid="group-sheet-expand-toggle">
              {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            </Button>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {group && <Badge variant="outline" className="text-[10px] border-sky-300 text-sky-700" data-testid="group-sheet-type">{sum.typeLabel}</Badge>}
            {conflicts.length > 0 && <Badge variant="outline" className="text-[10px] border-red-300 text-red-700" data-testid="group-sheet-conflicts">{conflicts.length} worker conflict{conflicts.length === 1 ? '' : 's'}</Badge>}
          </div>
        </div>
        <div className="flex-1 space-y-3 py-3">
          <div className="flex items-start gap-1.5 rounded-md border bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-700" data-testid="group-sheet-note"><Info className="w-3.5 h-3.5 shrink-0 mt-px" /> Services and billing are edited on each participant's own shift.</div>
          {error && <div className="text-xs text-red-700" data-testid="group-sheet-error">{error}</div>}
          {group && <GroupMiniGantt group={group} conflicts={conflicts} onOpenMember={openMember} />}
          <CalendarSharedGroupPanel shift={shift} staff={staff} onChanged={onUpdate} group={group} onReloadGroup={load} conflicts={conflicts} onOpenMember={openMember} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default CalendarGroupSheet;
